/**
 * Serviço de Núcleos
 *
 * Gerencia operações CRUD de núcleos usando PostgREST.
 */

import { postgrestClient } from "../lib/postgrest/client";

/**
 * Serviço de núcleos
 */
export const nucleosService = {
  /**
   * Lista núcleos com filtros.
   * Multitenant: passando negocioId (ex.: do perfil /me), filtra por tenant.
   * @param {Object} options
   * @param {Object} options.filters - Filtros (ex: { tipo: "eq.escola" })
   * @param {string} [options.negocioId] - UUID do tenant (perfil /me); aplica filtro por tenant
   * @param {string} options.order - Ordenação (ex: "nome.asc")
   * @param {number} options.limit - Limite de resultados
   * @param {number} options.offset - Offset para paginação
   * @param {boolean} options.includeCount - Se true, retorna { data, total }
   * @returns {Promise<Array|{data: Array, total: number}>}
   */
  async list(options = {}) {
    const { filters = {}, negocioId, order = "nome.asc", limit, offset, includeCount = false } = options;
    const MAX_SAFE_LIMIT = 200;
    const effectiveFilters = { ...filters };
    if (negocioId) {
      effectiveFilters.negocio_id = `eq.${negocioId}`;
    }
    // Blindagem global: evita consultas massivas que geram timeout e efeito cascata na aplicação.
    const safeLimit =
      typeof limit === "number" && Number.isFinite(limit)
        ? Math.min(limit, MAX_SAFE_LIMIT)
        : MAX_SAFE_LIMIT;
    const safeOffset = typeof offset === "number" && Number.isFinite(offset) ? offset : 0;
    // HOTFIX: evita a view pesada vw_nucleos_detalhados (estava causando timeout 504 e travando a app).
    // A listagem principal usa dados básicos de "nucleo" e preenche métricas com zero.
    let result;
    try {
      result = await postgrestClient.get("nucleo", {
        filters: effectiveFilters,
        order,
        limit: safeLimit,
        offset: safeOffset,
        includeCount,
      });
    } catch (error) {
      const status = error?.status || error?.statusCode;
      // Degradação segura: falha em núcleos não pode travar telas inteiras (turmas/alunos/professores).
      if (status === 504 || String(error?.message || "").includes("504")) {
        console.warn("[nucleosService.list] Timeout ao buscar núcleos. Retornando lista vazia para não bloquear a aplicação.");
        return includeCount ? { data: [], total: 0 } : [];
      }
      throw error;
    }

    const normalizeList = (rows) =>
      (Array.isArray(rows) ? rows : []).map((item) => ({
        ...item,
        id: item.id || item.nucleo_id,
        nucleo_id: item.nucleo_id || item.id,
        capacidade: item.capacidade ?? 0,
        capacidadeTotal: item.capacidadeTotal ?? item.capacidade ?? 0,
        ocupacao:
          typeof item.ocupacao === "number"
            ? { total: item.ocupacao }
            : item.ocupacao || { total: 0 },
      }));

    // Se includeCount, retorna { data, total }, senão retorna array direto
    if (includeCount && result && typeof result === 'object' && 'data' in result) {
      return {
        ...result,
        data: normalizeList(result.data),
      };
    }
    return normalizeList(result);
  },

  /**
   * Busca núcleo por ID
   * @param {string} id
   * @returns {Promise<Object>}
   */
  async getById(id) {
    const result = await postgrestClient.get("nucleos", {
      filters: { nucleo_id: `eq.${id}` },
    });
    return Array.isArray(result) ? result[0] : result;
  },

  /**
   * Cria novo núcleo
   * @param {Object} nucleoData
   * @returns {Promise<Object>}
   */
  async create(nucleoData) {
    const result = await postgrestClient.post("nucleos", nucleoData);
    return Array.isArray(result) ? result[0] : result;
  },

  /**
   * Atualiza núcleo
   * @param {string} id
   * @param {Object} nucleoData
   * @returns {Promise<Object>}
   */
  async update(id, nucleoData) {
    const result = await postgrestClient.patch("nucleos", { nucleo_id: id }, nucleoData);
    return Array.isArray(result) ? result[0] : result;
  },

  /**
   * Remove núcleo
   * @param {string} id
   * @returns {Promise<null>}
   */
  async delete(id) {
    return postgrestClient.delete("nucleos", { nucleo_id: id });
  },

  /**
   * Busca ocupação de um núcleo (RPC function)
   * @param {string} nucleoId
   * @returns {Promise<Object>}
   */
  async getOcupacao(nucleoId) {
    return postgrestClient.rpc("nucleo_ocupacao", {
      p_nucleo_id: nucleoId,
    });
  },

};

export default nucleosService;

